import type { SagaIterator } from 'redux-saga';
import { call, put, takeEvery } from 'redux-saga/effects';
import keyBy from 'lodash.keyby';
import type { Action } from 'ext/lib/collections';
import { INITIALIZE } from 'ext/root/root-actions';
import { getTypedContext } from 'ext/lib/saga-context';
import {
  reject,
  resolve,
  send,
  append,
  prune,
  flush,
  patterns,
} from './actions';
import type { Locale } from './types';

export function* fetchLocales(): SagaIterator {
  const logger = yield* getTypedContext('logger');
  try {
    const api = yield* getTypedContext('api');
    const response: Locale[] = yield call(api.getLocales);

    yield put(resolve(keyBy(response, 'id')));
  } catch (error) {
    yield put(reject(error as Error));
    logger.error(error);
  }
}

export function* createLocale({ payload }: Action): SagaIterator {
  const logger = yield* getTypedContext('logger');
  try {
    yield put(send());

    const api = yield* getTypedContext('api');
    const locale: Locale = yield call(api.createLocale, payload);

    yield put(append({ [locale.id]: locale }));
  } catch (error) {
    logger.error(error);
  }
}

export function* removeLocale({ payload }: Action): SagaIterator {
  const logger = yield* getTypedContext('logger');
  try {
    const { id } = payload;
    const api = yield* getTypedContext('api');

    yield call(api.removeLocale, id);
    yield put(prune(id));
  } catch (error) {
    logger.error(error);
  }
}

export function* updateLocale({ payload }: Action): SagaIterator {
  const logger = yield* getTypedContext('logger');
  try {
    const { id, delta } = payload;
    const api = yield* getTypedContext('api');

    yield call(api.updateLocale, id, delta);
    yield put(flush(id));
  } catch (error) {
    logger.error(error);
  }
}

export function* autoTranslateXliff({ payload }: Action): SagaIterator {
  const logger = yield* getTypedContext('logger');

  try {
    const { xliff } = payload;
    const api = yield* getTypedContext('api');

    // start loading
    yield put(send());

    const updatedXLIFF = yield call(api.autoTranslate, xliff);
    return updatedXLIFF;
  } catch (error) {
    yield put(reject(error as Error));
    logger.error(error);
    return error;
  }
}

export default function* LocalesSaga(): SagaIterator {
  yield takeEvery([INITIALIZE], fetchLocales);
  yield takeEvery(patterns.create, createLocale);
  yield takeEvery(patterns.remove, removeLocale);
  yield takeEvery(patterns.update, updateLocale);
}
